export const BASE_URL = process.env.REACT_APP_BASE_URL;

export const DASHBOARD_BASE_URL = {
  PROD: 'https://dashboard.focusbear.io',
  DEV: 'https://dashboard.local.dev:3000'
};

export const SUBSCRIPTION_INFO_KEY =
  'https://eyst-backend-prod-8th3.onrender.com/user';

export const DOWNLOAD_LINK = {
  MAC_APP: 'https://downloads.focusbear.io/FocusBearSetupMac.dmg',
  WINDOWS_APP: 'https://downloads.focusbear.io/FocusBearSetupWindows.exe',
  MOBILE: {
    ANDROID: 'https://play.google.com/store/apps/details?id=com.focusbear',
    IOS: 'https://apps.apple.com/us/app/focus-bear/id1673296334'
  }
};

export const WEBVIEW_ROUTES = {
  WEBVIEW_STATS: '/webview/stats',
  WEBVIEW_CHAT: '/webview/chat',
  WEBVIEW_TODO_LIST: '/webview/todo-list',
  WEBVIEW_MOTIVATIONAL_SUMMARY: '/webview/motivational-summary',
  WEBVIEW_MOBILE_MOTIVATIONAL_SUMMARY: '/webview-mobile/motivational-summary',
  WEBVIEW_TOOLS_TODO_LIST: '/webview/tools-todo-list',
  WEBVIEW_SURVEY: '/webview/survey',
  WEBVIEW_FOCUS_END: '/webview/focus-end',
  WEBVIEW_COURSE: '/webview/course',
  WEBVIEW_TO_DO_PLAYER: '/webview/todo-player',
  WEBVIEW_MANAGE_SUBSCRIPTION: '/webview/manage-subscription',
  WEBVIEW_ROUTINE_SUGGESTION: '/webview/routine-suggestion',
  WEBVIEW_ROUTINE_SUGGESTION_REVAMP: '/webview/routine-suggestion-revamp',
  WEBVIEW_ENROLLED_COURSES: '/webview/enrolled-courses',
  WEBVIEW_GET_SUPPORT: '/webview/get-support',
  WEBVIEW_MARKETPLACE: '/webview/marketplace',
  WEBVIEW_SETTINGS: '/webview/settings',
  WEBVIEW_NOTES: '/webview/notes',
  WEBVIEW_PROJECT_DETAIL: '/webview/projects'
};

export const ROUTES = {
  LOGIN: '/login',
  HOME: '/',
  DESKTOP_REDIRECT: '/desktop-redirect',
  PROFILE: '/profile',
  TEAM: '/team/:id',
  /** Class detail dashboard only — create/list live on the school team page. */
  CLASS: '/class/:id',
  MANAGED_BLOCKING: '/managed-blocking',
  MANAGED_BLOCKING_SCHOOL: '/managed-blocking/schools/:schoolId',
  MANAGED_BLOCKING_CHILD: '/managed-blocking/children/:studentId',
  /** Student API playground — URL-only; never add to nav. */
  CLASS_SESSION_PLAYGROUND: '/class-session',
  FOCUS_LOGS: '/focus-logs',
  SETTINGS: '/settings',
  MARKETPLACE: '/marketplace',
  LIBRARY: '/library',
  ADMIN: '/admin',
  ERROR: '/error',
  NOT_FOUND: '*',
  HABIT_PACK_DETAILS: '/habit-packs/:id',
  HABIT_PACK_SHARE: '/habit-packs/share/:id',
  HABIT_PACK_WELCOME: '/habit-packs/welcome-page',
  FOCUS_TEMPLATE_WELCOME: '/focus-mode-templates/welcome-page',
  FOCUS_TEMPLATE_DETAILS: '/focus-mode-templates/:id',
  FOCUS_TEMPLATE_SHARE: '/focus-mode-templates/share/:id',
  ROUTINE_PLAYER: 'routine-player',
  CHAT: '/chat',
  BACK_ONE_STEP: -1,
  MOTIVATIONAL_SUMMARY: '/motivational-summary',
  TODO: '/todo',
  DAY_PLAN: '/day-plan',
  NOTES: '/notes',
  ACCEPT_INVITE: '/accept-invite',
  ACCEPT_PROJECT_INVITE: '/accept-project-invite',
  PLATFORM_CALLBACK: '/callback/:platform',
  ZOHO_CALLBACK: '/callback/zoho',
  MONDAY_CALLBACK: '/callback/monday',
  CLICK_UP_CALLBACK: '/callback/clickup',
  ASANA_CALLBACK: '/callback/asana',
  JIRA_CALLBACK: '/callback/jira',
  TRELLO_CALLBACK: '/callback/trello',
  GOOGLE_CALLBACK: '/callback/google',
  MICROSOFT_CALLBACK: '/callback/microsoft',
  LOG_OUT: '/log-out', // @Description: it is dummy route & used only for checking the navbar dropdown children
  MANAGE_SUBSCRIPTION: '/manage-subscription',
  FORBIDDEN: '/forbidden',
  VERIFY_EMAIL: '/verify-email',
  RESET_PASSWORD: '/reset-password',
  ONBOARD_FLOW: '/onboard-flow',
  GUEST_ONBOARDING: '/guest-onboarding',
  INTEGRATIONS: '/integrations',
  ACCOUNTABILITY_BUDDY: '/accountability-buddy',
  ACCOUNTABILITY_BUDDY_ACCEPT: '/accountability-buddy/accept',
  ACCOUNTABILITY_BUDDY_APPROVE_UNLOCK:
    '/accountability-buddy/unlock-request/approve',
  MOBILE_APP_DOWNLOAD: '/mobile-app-download',
  DOWNLOAD_APPS: '/download-apps',
  PROJECTS: '/projects',
  PROJECT_DETAIL: '/projects/:id',
  /** Public Universal-Link fallback: opens the app when installed, else a download gateway. */
  VIEW_AUDHD_RESULTS: '/view-audhd-results',
  /** Agent panel framed inside Zoho Desk. */
  SUPPORT_PANEL: '/support-panel'
};

/** Custom URL schemes the native apps register (compose from SCHEME). */
const APP_URL_SCHEME = 'focusbear://';

export const APP_DEEP_LINKS = {
  LOGIN_FROM_WEB: `${APP_URL_SCHEME}login-from-web`,
  VIEW_AUDHD_RESULTS: `${APP_URL_SCHEME}${ROUTES.VIEW_AUDHD_RESULTS.slice(1)}`
} as const;

export const VALID_REDIRECT_PATHS = [
  ROUTES.SETTINGS,
  ROUTES.MARKETPLACE,
  ROUTES.LIBRARY,
  ROUTES.ADMIN,
  ROUTES.FOCUS_LOGS,
  ROUTES.PROFILE,
  ROUTES.TODO,
  ROUTES.NOTES,
  // ROUTES.ROUTINE_PLAYER,  // ROUTINE_PLAYER: 'routine-player', // Temporarily disabled - needs major changes due to latest changes like custom routines
  ROUTES.MANAGE_SUBSCRIPTION,
  ROUTES.HOME,
  ROUTES.ACCEPT_INVITE,
  ROUTES.ACCEPT_PROJECT_INVITE,
  ROUTES.ACCOUNTABILITY_BUDDY_ACCEPT,
  ROUTES.FOCUS_TEMPLATE_SHARE,
  ROUTES.HABIT_PACK_SHARE,
  ROUTES.FOCUS_TEMPLATE_WELCOME,
  ROUTES.HABIT_PACK_WELCOME,
  ROUTES.DESKTOP_REDIRECT,
  ROUTES.SUPPORT_PANEL
];

/** i18n keys — resolve with `t()` at render time, never at module scope. */
export const PAGES = {
  DASHBOARD: 'pages.dashboard',
  SUBSCRIPTION: 'pages.subscription',
  TEAM: 'pages.team',
  CLASS: 'pages.class',
  CLASS_SESSION_PLAYGROUND: 'pages.class_session_playground',
  STATS: 'pages.stats',
  HABITS: 'pages.habits',
  MARKETPLACE: 'pages.marketplace',
  LIBRARY: 'pages.library',
  SHARE: 'pages.share',
  ADMIN: 'pages.admin',
  NOT_FOUND: 'pages.not_found',
  ERROR: 'pages.error',
  WELCOME: 'pages.welcome',
  HABIT_PACK: 'pages.habit_pack',
  FOCUS_TEMPLATE: 'pages.focus_template',
  PROFILE: 'pages.profile',
  TO_DO: 'pages.to_do',
  FORBIDDEN: 'pages.forbidden',
  INTEGRATIONS: 'pages.integrations',
  ACCOUNTABILITY_BUDDY: 'pages.accountability_buddy',
  DAY_PLAN: 'pages.day_plan',
  DAY_PLAN_WITH_DATE: 'pages.day_plan_with_date',
  NOTES: 'pages.notes',
  PROJECTS: 'pages.projects',
  PROJECT: 'pages.project'
};

export const PRIVACY_POLICY = {
  EN: '/en/privacy-policy',
  ES: '/es/politica-de-privacidad'
};

export const LATE_NO_MORE = 'https://focusbear.io/en/late-no-more';
export const FOCUS_BEAR_LANDING_PAGE = 'https://www.focusbear.io';
export const FOCUS_BEAR_DASHBOARD = 'https://dashboard.focusbear.io';
export const POST_HOG_API_HOST = 'https://us.i.posthog.com';

export const PUBLIC_ROUTES = [
  ROUTES.HABIT_PACK_SHARE,
  ROUTES.FOCUS_TEMPLATE_SHARE,
  ROUTES.VERIFY_EMAIL,
  ROUTES.RESET_PASSWORD,
  ROUTES.DESKTOP_REDIRECT,
  ROUTES.MARKETPLACE,
  ROUTES.FOCUS_TEMPLATE_WELCOME
];

export const PACK_FOCUS_ID_INDEX = {
  WEBVIEW: 2,
  DASHBOARD: 1,
  LANDING_PAGE: 2
};

/** Path segment index for project id: `/projects/:id` vs `/webview/projects/:id`. */
export const PROJECT_ID_PATH_INDEX = {
  WEBVIEW: 2,
  DASHBOARD: 1
} as const;

export const MAIN_LAYOUT_EXCLUDED_ROUTES = [
  ROUTES.ONBOARD_FLOW,
  ROUTES.VERIFY_EMAIL,
  ROUTES.RESET_PASSWORD,
  ROUTES.ACCEPT_INVITE,
  ROUTES.ACCEPT_PROJECT_INVITE,
  ROUTES.DESKTOP_REDIRECT,
  ROUTES.MOBILE_APP_DOWNLOAD,
  ROUTES.DOWNLOAD_APPS,
  ROUTES.FOCUS_LOGS
];

export const BACK_ONE_STEP = -1;
